/**
 * KYP V3 API Service
 * Handles all API calls related to the stacked KYP functionality
 */

import axios from 'axios';

// Helper function to get base URL
// Helper function to get base URL
const getBaseUrl = () => import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
// Helper function to create request headers with auth token
const createHeaders = (token: string) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

// Create a custom axios instance with extended timeout for long-running KYP operations
const kypV3ApiClient = axios.create({
  baseURL: getBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  timeout: 300000, // 15 minutes
});

/**
 * Interface for Individual Summary
 */
export interface IndividualSummary {
  article_id: number;
  title: string;
  url: string;
  summary: string;
  relevance_score?: number;
  word_count: number;
  cached?: boolean;
}

/**
 * Interface for KYP V3 Summary Response
 */
export interface KypV3SummaryResponse {
  status: string;
  player_id: string;
  player_name?: string;
  search_id?: number;
  summary: string;
  article_count: number;
  summary_count: number;
  individual_summaries?: IndividualSummary[];
  execution_time_seconds?: number;
  metadata: Record<string, any>;
}

/**
 * Interface for KYP V3 Status Response
 */
export interface KypV3StatusResponse {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  player_id: string;
  search_id?: string;
  message?: string;
  progress?: number;
  current_step?: string;
  estimated_time_remaining_seconds?: number;
}

export interface CategoryStatus {
  [categoryName: string]: boolean;
}

export interface CategoryData {
  category_name: string;
  profile_content: any;
  processing_status: 'completed' | 'failed';
  error_message?: string;
  ordered_article_summaries?: OrderedArticleSummary[];
}

export interface OrderedArticleSummary {
  id: string;
  summary_text: string;
  categories: string[];
  relevance_score?: number;
  article_order: number;
  article_url?: string;
  title?: string;
  sources?: ArticleSource[];
}

export interface ArticleSource {
  url: string;
  title: string;
  relevance_score?: number;
}

export interface ParallelCategorySearchResponse {
  status: 'started';
  search_id: string;
  message: string;
}

/**
 * KYP V3 Service for interacting with the stacked KYP API
 */
export const kypV3ApiService = {
  /**
   * Execute the stacked KYP search and summarization pipeline
   * 
   * @param playerId - ID of the player to search for
   * @param playerName - Name of the player (for display purposes)
   * @param token - Authentication token
   * @param minRelevanceThreshold - Minimum relevance score threshold (0.0-1.0)
   * @param forceResummary - Force re-summarization of all articles
   * @param onProgress - Optional callback for progress updates
   * @returns Promise with the search and summary results
   */
  executeStackedKYPSearch: async (
    playerId: string,
    playerName: string,
    token: string,
    minRelevanceThreshold: number = 0.3,
    forceResummary: boolean = false,
    onProgress?: (status: KypV3StatusResponse) => void
  ): Promise<KypV3SummaryResponse> => {
    const params = new URLSearchParams();
    
    params.append('min_relevance_threshold', minRelevanceThreshold.toString());
    
    if (forceResummary) {
      params.append('force_resummary', 'true');
    }
    
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const url = `/ai/v1/${playerId}/kyp_search_v2${queryString}`;
    
    console.log(`[KYP V3] Executing stacked KYP search with URL: ${url}`);
    
    try {
      // Use the extended timeout client for this long-running operation
      const response = await kypV3ApiClient.post(url, {}, {
        headers: createHeaders(token)
      });
      
      console.log(`[KYP V3] Search completed successfully`);
      return response.data;
    } catch (error: any) {
      console.error(`[KYP V3] Error executing search:`, error);
      
      if (error.response) {
        console.error(`[KYP V3] Response status: ${error.response.status}`);
        console.error(`[KYP V3] Response data:`, error.response.data);
      }
      
      // Check if this is a timeout error
      if (error.code === 'ECONNABORTED' || (error.message && error.message.includes('timeout'))) {
        console.log('[KYP V3] Request timed out, switching to polling mode');
        
        // If we have a timeout, switch to polling mode to check status
        return await pollForResults(playerId, token, onProgress);
      }
      
      // Re-throw other errors
      throw error;
    }
  },
  
  /**
   * Get KYP V3 summary for a player using existing articles
   * 
   * @param playerId - ID of the player to summarize
   * @param token - Authentication token
   * @param minRelevanceThreshold - Minimum relevance score threshold
   * @param forceResummary - Force re-summarization of all articles
   * @param onProgress - Optional callback for progress updates
   * @returns Promise with the summary results
   */
  getStackedKYPSummary: async (
    playerId: string,
    token: string,
    minRelevanceThreshold: number = 0.3,
    forceResummary: boolean = false,
    onProgress?: (status: KypV3StatusResponse) => void
  ): Promise<KypV3SummaryResponse> => {
    const params = new URLSearchParams();
    
    params.append('min_relevance_threshold', minRelevanceThreshold.toString());
    
    if (forceResummary) {
      params.append('force_resummary', 'true');
    }
    
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const url = `/ai/v1/${playerId}/kyp_summary_v2${queryString}`;
    
    console.log(`[KYP V3] Getting summary with URL: ${url}`);
    
    try {
      // Use the extended timeout client for this long-running operation
      const response = await kypV3ApiClient.get(url, {
        headers: createHeaders(token)
      });
      
      console.log(`[KYP V3] Summary retrieved successfully`);
      return response.data;
    } catch (error: any) {
      console.error(`[KYP V3] Error getting summary:`, error);
      
      if (error.response) {
        console.error(`[KYP V3] Response status: ${error.response.status}`);
        console.error(`[KYP V3] Response data:`, error.response.data);
      }
      
      // Check if this is a timeout error
      if (error.code === 'ECONNABORTED' || (error.message && error.message.includes('timeout'))) {
        console.log('[KYP V3] Request timed out, switching to polling mode');
        
        // If we have a timeout, switch to polling mode to check status
        return await pollForResults(playerId, token, onProgress);
      }
      
      // Re-throw other errors
      throw error;
    }
  },
  
  /**
   * Check the status of a KYP V3 operation
   * 
   * @param playerId - ID of the player
   * @param token - Authentication token
   * @returns Promise with the status response
   */
  checkStackedKYPStatus: async (playerId: string, token: string): Promise<KypV3StatusResponse> => {
    const url = `/ai/v1/${playerId}/kyp_status`;
    console.log(`[KYP V3] Checking status with URL: ${url}`);
    
    try {
      const response = await kypV3ApiClient.get(url, {
        headers: createHeaders(token)
      });
      console.log(`[KYP V3] Status check successful:`, response.data);
      return response.data;
    } catch (error: any) {
      console.error('[KYP V3] Error checking status:', error);
      
      if (error.response) {
        console.error(`[KYP V3] Response status: ${error.response.status}`);
        console.error(`[KYP V3] Response data:`, error.response.data);
      }
      
      throw error;
    }
  },
  
  /**
   * Get results of a completed KYP V3 operation
   * 
   * @param playerId - ID of the player
   * @param token - Authentication token
   * @returns Promise with the results
   */
  getStackedKYPResults: async (playerId: string, token: string): Promise<KypV3SummaryResponse> => {
    const url = `/ai/v1/${playerId}/kyp_result`;
    console.log(`[KYP V3] Getting results with URL: ${url}`);
    
    try {
      const response = await kypV3ApiClient.get(url, {
        headers: createHeaders(token)
      });
      console.log(`[KYP V3] Results retrieved successfully`);
      return response.data;
    } catch (error: any) {
      console.error('[KYP V3] Error getting results:', error);
      
      if (error.response) {
        console.error(`[KYP V3] Response status: ${error.response.status}`);
        console.error(`[KYP V3] Response data:`, error.response.data);
      }
      
      throw error;
    }
  },

  getCategoryStatuses: async (searchId: string, token: string, signal?: AbortSignal): Promise<CategoryStatus> => {
    const url = `/ai/v1/kyp/summarised_search/${searchId}/status`;
    const fullUrl = `${getBaseUrl()}${url}`;
    console.log(`[KYP_POLLING] Requesting category statuses from: ${fullUrl}`);
    
    try {
      const response = await kypV3ApiClient.get(url, {
        headers: createHeaders(token),
        signal,
      });
      
      console.log(`[KYP_POLLING] Successfully received statuses for searchId ${searchId}:`, response.data);
      return response.data.category_statuses;
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log(`[KYP_POLLING] Request to ${fullUrl} was canceled.`);
      } else {
        console.error(`[KYP_POLLING] Failed to get category statuses from ${fullUrl}:`, {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          response: error.response?.data,
        });
      }
      throw error;
    }
  },

  getCompletedCategories: async (searchId: string, token: string, signal?: AbortSignal): Promise<CategoryData[]> => {
    const url = `/ai/v1/kyp/search/${searchId}/categories`;
    const fullUrl = `${getBaseUrl()}${url}`;
    console.log(`[KYP_POLLING] Fetching completed categories from: ${fullUrl}`);
    
    try {
      const response = await kypV3ApiClient.get(url, {
        headers: createHeaders(token),
        signal,
      });
      
      console.log(`[KYP_POLLING] Successfully received completed categories for searchId ${searchId}:`, response.data);
      return response.data;
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log(`[KYP_POLLING] Request to ${fullUrl} was canceled.`);
      } else {
        console.error(`[KYP_POLLING] Failed to fetch completed categories from ${fullUrl}:`, {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          response: error.response?.data,
        });
      }
      throw error;
    }
  },

  executeParallelCategorySearch: async (
    playerId: string,
    playerName: string,
    token: string,
    forceResummary: boolean = false
  ): Promise<ParallelCategorySearchResponse> => {
    const url = `/ai/v1/kyp/summarised_search`;
    const response = await kypV3ApiClient.post(
      url,
      { player_id: playerId, force_resummary: forceResummary },
      { headers: createHeaders(token) }
    );
    return response.data;
  },

  executeParallelCategoryTest: async (
    playerId: string,
    playerName: string,
    token: string,
    forceResummary: boolean = false
  ): Promise<ParallelCategorySearchResponse> => {
    const url = `/ai/v1/kyp/test_parallel_categories`;
    const response = await kypV3ApiClient.post(
      url,
      { player_id: playerId, force_resummary: forceResummary },
      { headers: createHeaders(token) }
    );
    return response.data;
  },

  executeMockParallelCategorySearch: async (
    playerId: string,
    token: string
  ): Promise<ParallelCategorySearchResponse> => {
    const url = `/ai/v1/kyp/mock_search`;
    console.log('[DEBUG] Mock search starting:', {
      playerId,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'No token',
      url: url
    });
    const response = await kypV3ApiClient.post(
      url,
      { player_id: playerId },
      { headers: createHeaders(token) }
    );
    return response.data;
  },

  getMockCategoryStatuses: async (searchId: string, token: string, signal?: AbortSignal): Promise<CategoryStatus> => {
    const url = `/ai/v1/kyp/mock_search/${searchId}/status`;
    const response = await kypV3ApiClient.get(url, {
      headers: createHeaders(token),
      signal,
    });
    return response.data.category_statuses;
  },

  getMockCompletedCategories: async (searchId: string, token: string, signal?: AbortSignal): Promise<CategoryData[]> => {
    const url = `/ai/v1/kyp/mock_search/${searchId}/categories`;
    const response = await kypV3ApiClient.get(url, {
      headers: createHeaders(token),
      signal,
    });
    return response.data;
  },

};

/**
 * Poll for KYP V3 results until completion or failure
 * 
 * @param playerId - ID of the player
 * @param token - Authentication token
 * @param onProgress - Optional callback for progress updates
 * @returns Promise with the summary results
 */
async function pollForResults(
  playerId: string,
  token: string,
  onProgress?: (status: KypV3StatusResponse) => void
): Promise<KypV3SummaryResponse> {
  // Initial delay before first poll (in ms)
  const initialDelay = 2000;
  
  // Maximum number of polling attempts
  const maxAttempts = 60;
  
  // Delay between polling attempts (in ms)
  const pollingInterval = 5000;
  
  console.log(`[KYP V3] Starting polling for player ${playerId} with ${maxAttempts} attempts`);
  
  // Wait for the initial delay
  await new Promise(resolve => setTimeout(resolve, initialDelay));
  
  // Start polling
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    console.log(`[KYP V3] Polling attempt ${attempt + 1}/${maxAttempts}`);
    
    try {
      // Check the status
      const status = await kypV3Service.checkStackedKYPStatus(playerId, token);
      
      console.log(`[KYP V3] Poll status: ${status.status}, progress: ${status.progress}, step: ${status.current_step}`);
      
      // Call the progress callback if provided
      if (onProgress) {
        onProgress(status);
      }
      
      // If the operation is completed, get the results
      if (status.status === 'completed') {
        console.log(`[KYP V3] Operation completed, getting results`);
        
        // Get the final results
        return await kypV3ApiService.getStackedKYPResults(playerId, token);
      }
      
      // If the operation failed, throw an error
      if (status.status === 'failed') {
        console.error(`[KYP V3] Operation failed: ${status.message}`);
        throw new Error(status.message || 'KYP V3 operation failed');
      }
      
      console.log(`[KYP V3] Operation still in progress, waiting ${pollingInterval}ms before next attempt`);
      
      // Wait for the polling interval
      await new Promise(resolve => setTimeout(resolve, pollingInterval));
    } catch (error: any) {
      console.error(`[KYP V3] Error polling for results (attempt ${attempt + 1}/${maxAttempts}):`, error);
      
      if (error.response) {
        console.error(`[KYP V3] Response status: ${error.response.status}`);
        console.error(`[KYP V3] Response data:`, error.response.data);
      }
      
      // If this is the last attempt, throw the error
      if (attempt === maxAttempts - 1) {
        throw error;
      }
      
      console.log(`[KYP V3] Retrying after ${pollingInterval}ms`);
      
      // Wait for the polling interval before retrying
      await new Promise(resolve => setTimeout(resolve, pollingInterval));
    }
  }
  
  // If we've reached the maximum number of attempts, throw an error
  const timeoutError = new Error(`KYP V3 operation timed out after ${maxAttempts} polling attempts`);
  console.error(`[KYP V3] ${timeoutError.message}`);
  throw timeoutError;
}